/**
 * supabaseSubscriptionRepository.ts — The only file in the subscription feature
 * that imports supabase. All table names, column names, and RPC names live here.
 */

import { supabase } from '@/lib/supabase';
import type {
  SubscriptionRepository,
  SubscriptionModel,
  PremiumFeatureModel,
  RawSubscriptionRow,
  RawPremiumFeatureRow,
  SubscriptionStatusDB,
  PremiumGateModeDB,
} from './types';

function toSubscriptionModel(row: RawSubscriptionRow): SubscriptionModel {
  return {
    id:                   row.id,
    userId:               row.user_id,
    stripeSubscriptionId: row.stripe_subscription_id,
    stripeCustomerId:     row.stripe_customer_id,
    stripePriceId:        row.stripe_price_id,
    status:               row.status as SubscriptionStatusDB,
    currentPeriodStart:   row.current_period_start ? new Date(row.current_period_start) : null,
    currentPeriodEnd:     row.current_period_end   ? new Date(row.current_period_end)   : null,
    cancelAtPeriodEnd:    row.cancel_at_period_end,
    canceledAt:           row.canceled_at ? new Date(row.canceled_at) : null,
    trialEnd:             row.trial_end   ? new Date(row.trial_end)   : null,
    createdAt:            new Date(row.created_at),
    updatedAt:            new Date(row.updated_at),
    isSuspended:          row.is_suspended === true,
    pastDueStartedAt:     row.past_due_started_at ? new Date(row.past_due_started_at) : null,
  };
}

function toPremiumFeatureModel(row: RawPremiumFeatureRow): PremiumFeatureModel {
  return {
    featureKey: row.feature_key,
    gateMode:   row.gate_mode as PremiumGateModeDB,
    graceEndAt: row.grace_end_at ? new Date(row.grace_end_at) : null,
    comingSoon: row.coming_soon,
  };
}

export const subscriptionRepository: SubscriptionRepository = {
  async fetchMySubscription(userId) {
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .in('status', ['active', 'trialing', 'past_due', 'canceled', 'unpaid'])
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return data ? toSubscriptionModel(data as RawSubscriptionRow) : null;
  },

  async fetchPremiumFeatures() {
    const { data, error } = await supabase
      .from('premium_features')
      .select('feature_key, gate_mode, grace_end_at, coming_soon')
      .order('feature_key');

    if (error) throw error;
    return (data ?? []).map(row => toPremiumFeatureModel(row as RawPremiumFeatureRow));
  },

  async fetchMyAccountProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_instructor')
      .eq('id', userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return { isInstructor: data.is_instructor === true };
  },

  async canUsePremiumFeature(userId, featureKey) {
    const { data, error } = await supabase.rpc('can_use_premium_feature', {
      p_user: userId,
      p_feature: featureKey,
    });

    if (error) throw error;
    return data as boolean;
  },
};
